import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useSelectedProduct } from 'main-layout-op/_hooks/useSelectedProduct';
import { useSecondarySidebarState } from 'main-layout-op/_providers/_hooks/useSecondarySidebarState';
import { Product } from '@cast/console-types';

import { MainLayoutState } from '../_types';

type Props = PropsWithChildren<{
  noSidebar: boolean;
}>;

const MainLayoutContext = createContext<MainLayoutState>({
  isInsideMainLayout: false,
} as never);

export const MainLayoutProvider = ({ children, noSidebar }: Props) => {
  const selectedProduct = useSelectedProduct();
  const [selectedSecondarySidebarProduct, setSelectedSecondarySidebarProduct] =
    useState<Product>(selectedProduct ?? 'automation');
  const sidebarState = useSecondarySidebarState(noSidebar);
  const sidebarRef = useRef<HTMLDivElement | null>(null);
  const [isProductPickerOpen, setIsProductPickerOpen] = useState(false);
  const justCollapsedRef = useRef(false);

  const openProductPicker = useCallback(() => setIsProductPickerOpen(true), []);
  const closeProductPicker = useCallback(
    () => setIsProductPickerOpen(false),
    []
  );

  useEffect(() => {
    if (selectedProduct) {
      setSelectedSecondarySidebarProduct(selectedProduct);
    }
  }, [selectedProduct]);

  const collapseSecondarySidebar = useCallback(() => {
    justCollapsedRef.current = true;
    sidebarState.collapseSidebar();
  }, [sidebarState]);

  const handleSidebarEnter = useCallback(() => {
    if (justCollapsedRef.current) {
      justCollapsedRef.current = false;
      return;
    }
    if (sidebarState.state === 'collapsed') {
      sidebarState.expandSidebar();
    }
  }, [sidebarState]);

  const handleSidebarLeave = useCallback(() => {
    justCollapsedRef.current = false;
    if (sidebarState.state === 'expanded') {
      sidebarState.collapseSidebar();
    }
  }, [sidebarState]);

  const handleProductSelect = useCallback(
    (product: Product) => {
      setSelectedSecondarySidebarProduct(product);
      setIsProductPickerOpen(false);
      sidebarState.pinSidebar();
    },
    [sidebarState]
  );

  return (
    <MainLayoutContext.Provider
      value={{
        noSidebar,
        secondarySidebarState: sidebarState.state,
        collapseSecondarySidebar: collapseSecondarySidebar,
        expandSecondarySidebar: sidebarState.expandSidebar,
        pinSecondarySidebar: sidebarState.pinSidebar,
        isPinned: sidebarState.pinned,
        isInsideMainLayout: true,
        sidebarRef,
        selectedSidebarProduct: selectedSecondarySidebarProduct,
        handleSidebarEnter,
        handleSidebarLeave,
        handleProductSelect,
        isProductPickerOpen,
        openProductPicker,
        closeProductPicker,
      }}
    >
      {children}
    </MainLayoutContext.Provider>
  );
};

export const useMainLayout = () => useContext(MainLayoutContext);
